import React, { useEffect, useRef, useState } from "react";
import { Editor } from '@tinymce/tinymce-react';
import { apiRequestWithAuth } from "../../../utils/api";
import { renderLatex } from "../../../utils/latex";

// Dùng KaTeX auto-render bên trong iframe của TinyMCE
const KATEX_DELIMITERS = [
    { left: '$$', right: '$$', display: true },
    { left: '\\[', right: '\\]', display: true },
    { left: '$', right: '$', display: false },
    { left: '\\(', right: '\\)', display: false },
];

const renderMathInEditor = (doc: Document) => {
    const win = doc.defaultView as any;
    if (win?.renderMathInElement && win?.katex) {
        try {
            win.renderMathInElement(doc.body, {
                delimiters: KATEX_DELIMITERS,
                throwOnError: false,
                trust: true,
            });
        } catch (e) {
            // ignore render errors
        }
    }
};

type MyEditor = {
    label?: string;
    content: string;
    onChange?: (value: string) => void;
    onBlur?: (value: string) => void;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    rows?: number;
};

const MyEditor5 = ({
                       label,
                       content,
                       onChange,
                       onBlur,
                       error,
                       required = false,
                       disabled = false,
                       placeholder = '',
                       rows = 1,
                       className = ''
                   }: MyEditor) => {
    const [textValue, setTextValue] = useState(content || '');
    const [isEditorLoaded, setIsEditorLoaded] = useState(false);
    const [clickPosition, setClickPosition] = useState<number | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const divRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<any>(null);
    // Lưu raw LaTeX value riêng, không bị ảnh hưởng bởi KaTeX DOM render
    const rawValueRef = useRef<string>(content || '');

    useEffect(() => {
        setTextValue(content || '');
        rawValueRef.current = content || '';
    }, [content]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleImageUpload: any = async (blobInfo: any, success: any, failure: any) => {
        const formData = new FormData();
        formData.append('file', blobInfo.blob());
        formData.append('subject', 'post_image');

        try {
            const res = await apiRequestWithAuth('/media/file', 'POST', formData);

            if (res.messageCode === 200 && res.result?.publicPath) {
                success(res.result.publicPath);
            } else {
                failure('Upload failed: Invalid response');
            }
        } catch (e) {
            console.error('Upload failed', e);
            failure('Upload failed: Exception');
        }
    };

    const handleKeyUp = (content: any) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            onBlur?.(content);
        }, 500) as any;
    };

    const getCaretPositionFromClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!divRef.current) return 0;

        // try modern and legacy APIs
        let range: any = null;
        if ((document as any).caretPositionFromPoint) {
            const pos: any = (document as any).caretPositionFromPoint(e.clientX, e.clientY);
            if (pos) {
                const rangeObj = document.createRange();
                rangeObj.setStart(pos.offsetNode || pos.node, pos.offset);
                range = rangeObj;
            }
        } else if ((document as any).caretRangeFromPoint) {
            range = (document as any).caretRangeFromPoint(e.clientX, e.clientY);
        }

        if (range) {
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(divRef.current);
            preCaretRange.setEnd(range.endContainer, range.endOffset);
            return preCaretRange.toString().length;
        }

        return 0;
    };

    const handleFocus = (e: React.MouseEvent<HTMLDivElement>) => {
        const dialogOpen = document.querySelector('.wrs_modal_dialogContainer.wrs_stack');
        if (dialogOpen) {
            return
        }
        if (disabled) return;
        const position = getCaretPositionFromClick(e);
        setClickPosition(position);
        setIsEditorLoaded(true);
    };

    const setCursorPosition = (editor: any, position: number | null) => {
        if (position === null || position === undefined) return;

        setTimeout(() => {
            const contentText = editor.getContent({ format: 'text' });
            const clampedPosition = Math.min(position, contentText.length);

            const body = editor.getBody();
            const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, null);

            let currentPos = 0;
            let textNode = walker.nextNode();

            while (textNode) {
                const nodeLength = textNode.textContent?.length || 0;

                if (currentPos + nodeLength >= clampedPosition) {
                    const range = editor.dom.createRng();
                    range.setStart(textNode, clampedPosition - currentPos);
                    range.setEnd(textNode, clampedPosition - currentPos);

                    editor.selection.setRng(range);
                    editor.focus();
                    break;
                }

                currentPos += nodeLength;
                textNode = walker.nextNode();
            }
        }, 50);
    };

    return (
        <div className={className}>
            {!isEditorLoaded || disabled ? (
                <div
                    ref={divRef}
                    onClick={handleFocus}
                    style={{
                        width: '100%',
                        minHeight: Math.max(48, rows * 24),
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        cursor: disabled ? 'default' : 'text',
                        backgroundColor: disabled ? '#f5f5f5' : 'white',
                        alignItems: 'center',
                    }}
                    dangerouslySetInnerHTML={{ __html: renderLatex(textValue) || `<div class="text-gray-400">${placeholder || 'Click to edit'}</div>` }}
                    tabIndex={disabled ? -1 : 0}
                    onKeyDown={(e) => {
                        if (disabled) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsEditorLoaded(true);
                        }
                    }}
                />
            ) : (
                React.createElement(Editor as any, {
                    apiKey: "v18zpfaevexdq35gdhot3cwcapw0eg5ljbr5s6vvzi9zkeau",
                    tinymceScriptSrc: "https://cdn.tiny.cloud/1/v18zpfaevexdq35gdhot3cwcapw0eg5ljbr5s6vvzi9zkeau/tinymce/5/tinymce.min.js",
                    onInit: (_: any, editor: any) => {
                        editorRef.current = editor;
                        setCursorPosition(editor, clickPosition);
                    },
                    init: {
                        external_plugins: {
                            tiny_mce_wiris: "https://www.wiris.net/demo/plugins/tiny_mce/plugin.js"
                        },
                        plugins: 'powerpaste autoresize tiny_mce_wiris anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount',
                        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat',
                        images_upload_handler: handleImageUpload,
                        automatic_uploads: true,
                        images_reuse_filename: true,
                        file_picker_types: 'image',
                        powerpaste_word_import: 'merge',
                        powerpaste_keep_unsupported_src: true,
                        content_css: 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css',
                        content_style: `
                            body { font-family: sans-serif; font-size: 14px; }
                            .katex-display { overflow-x: auto; overflow-y: hidden; }
                        `,
                        setup: (editor: any) => {
                            // Inject KaTeX for display-only rendering inside the editor iframe
                            editor.on('init', () => {
                                const doc = editor.getDoc();
                                if (!doc) return;

                                const script = doc.createElement('script');
                                script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.js';
                                script.onload = () => {
                                    const autoRenderScript = doc.createElement('script');
                                    autoRenderScript.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js';
                                    autoRenderScript.onload = () => {
                                        renderMathInEditor(doc);
                                    };
                                    doc.head.appendChild(autoRenderScript);
                                };
                                doc.head.appendChild(script);
                            });
                        },
                    },
                    value: textValue,
                    onEditorChange: (value: any, editor: any) => {
                        // Get raw content (without KaTeX-rendered HTML) from the editor
                        // KaTeX only runs on 'init', not on every keystroke, so value here is still raw
                        rawValueRef.current = value;
                        setTextValue(value);
                        onChange?.(value);
                    },
                    onKeyUp: (_: any, editor: any) => {
                        const currentContent = editor.getContent();
                        rawValueRef.current = currentContent;
                        handleKeyUp(currentContent);
                    },
                    onBlur: (_: any, editor: any) => {
                        setTimeout(function () {
                            const dialogOpen = document.querySelector('.wrs_modal_dialogContainer.wrs_stack');
                            if (dialogOpen) {
                                return;
                            }
                            const currentContent = editor.getContent();
                            rawValueRef.current = currentContent;
                            onBlur?.(currentContent);
                            setIsEditorLoaded(false);
                            setClickPosition(null);
                        }, 200);
                    },
                })
            )}
        </div>
    );
}

export default MyEditor5;
